import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TruckAnnouncementService } from '../../core/api/truck-announcement.service';
import { ReferenceDataService } from '../../core/api/reference-data.service';
import { TruckAnnouncementPin } from '../../shared/models/truck-announcement';
import { LookupOption } from '../../shared/models/lookup-option';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ 'ta.pinTitle' | t }}</h1>
      </header>
      @if (loading()) { <div class="skeleton-detail">
        <div class="skeleton-heading"></div>
        <div class="skeleton-line w-70"></div>
        <div class="skeleton-line w-55"></div>
        <div class="skeleton-line w-85"></div>
        <div class="skeleton-line w-40"></div>
      </div> }
      @if (error()) { <p class="error notice">{{ error() | t }}</p> }
      @if (!loading()) {
        <form class="form-card" [formGroup]="form" (ngSubmit)="save()">
          <div class="form-grid">
            <label>{{ 'ta.pin' | t }}
              <input formControlName="pin" />
            </label>

            <label>{{ 'ta.licencePlate' | t }}
              @if (directPortEntrance()) {
                <select formControlName="frontVehicleCodeId">
                  <option value="">{{ 'ta.selectVehicle' | t }}</option>
                  @for (v of vehicleOptions(); track v.Id) { <option [value]="v.Id">{{ v.Name || v.Code }}</option> }
                </select>
                @if (loadingVehicles()) { <span class="field-hint">{{ 'ta.serviceRequestSearching' | t }}</span> }
              } @else {
                <input formControlName="licencePlate" />
              }
            </label>

            <label>{{ 'ta.realizedLicencePlate' | t }}
              <input formControlName="realizedLicencePlate" />
              <span class="field-hint">{{ 'ta.systemManaged' | t }}</span>
            </label>

            <label>{{ 'ta.permitUserBadgeNumber' | t }}
              <select formControlName="permitUserBadgeNumber">
                <option value="">{{ 'ta.selectBadge' | t }}</option>
                @for (b of permitBadges(); track b.Id) { <option [value]="b['Badge'] ?? b.Code">{{ b['Badge'] ?? b.Code }} — {{ b.Name }}</option> }
              </select>
            </label>

            <label>{{ 'ta.announcementFrom' | t }}
              <input type="datetime-local" formControlName="announcementFrom" />
              <span class="field-hint">{{ 'ta.systemManaged' | t }}</span>
            </label>
            <label>{{ 'ta.announcementTo' | t }}
              <input type="datetime-local" formControlName="announcementTo" />
              <span class="field-hint">{{ 'ta.systemManaged' | t }}</span>
            </label>
          </div>

          <div class="form-grid">
            <label>{{ 'ta.serviceRequest' | t }}
              <input formControlName="serviceRequest" />
            </label>
            <label>{{ 'ta.serviceRequestPCSId' | t }}
              <input type="number" formControlName="serviceRequestPCSId" />
            </label>
          </div>

          <div class="form-actions">
            <a class="button secondary" [routerLink]="backLink()">{{ 'common.cancel' | t }}</a>
            <button class="button" type="submit" [disabled]="saving()">{{ (saving() ? 'common.saving' : 'common.save') | t }}</button>
          </div>
        </form>
      }
    </main>
  `
})
export class TruckAnnouncementPinEditPage implements OnInit {
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly directPortEntrance = signal(false);
  readonly vehicleOptions = signal<LookupOption[]>([]);
  readonly loadingVehicles = signal(false);
  readonly permitBadges = signal<LookupOption[]>([]);
  announcementId = '';
  private pinId = '';
  private operatorCode = '';
  private savedFrontVehicleCodeId: number | null = null;

  backLink(): string[] {
    return this.announcementId
      ? ['/truck-announcements', this.announcementId, 'pins', this.pinId]
      : ['/truck-announcement-pins', this.pinId];
  }
  private readonly fb = inject(FormBuilder);

  readonly form = this.fb.group({
    pin: [''],
    licencePlate: [''],
    frontVehicleCodeId: ['' as string | number],
    realizedLicencePlate: [{ value: '', disabled: true }],
    permitUserBadgeNumber: [''],
    announcementFrom: [{ value: '', disabled: true }],
    announcementTo: [{ value: '', disabled: true }],
    serviceRequest: [''],
    serviceRequestPCSId: [null as number | null]
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly api: TruckAnnouncementService,
    private readonly refs: ReferenceDataService
  ) {}

  ngOnInit(): void {
    this.announcementId = this.route.snapshot.paramMap.get('id') ?? '';
    this.pinId = this.route.snapshot.paramMap.get('pinId') ?? '';
    if (!this.pinId) { this.error.set('ta.pinLoadError'); this.loading.set(false); return; }

    this.refs.permitUserBadges().subscribe({ next: badges => this.permitBadges.set(badges), error: () => {} });

    this.api.getPin(this.pinId).subscribe({
      next: pin => {
        this.patch(pin);
        this.operatorCode = pin.TerminalOperatorCode ?? '';
        this.savedFrontVehicleCodeId = pin.FrontVehicleCodeId ?? null;
        this.loading.set(false);
        if (this.announcementId) this.loadParentDirectPortEntrance();
      },
      error: err => { this.error.set(err?.error?.Message ?? err?.error?.message ?? 'ta.pinLoadError'); this.loading.set(false); }
    });
  }

  private loadParentDirectPortEntrance(): void {
    this.api.get(this.announcementId).subscribe({
      next: item => {
        const dpe = item.DirectPortEntrance ?? false;
        this.directPortEntrance.set(dpe);
        if (dpe) this.loadVehicleOptions();
      },
      error: () => {}
    });
  }

  private loadVehicleOptions(): void {
    this.loadingVehicles.set(true);
    this.refs.truckModuleVehicles(this.operatorCode || undefined).subscribe({
      next: vehicles => {
        this.vehicleOptions.set(vehicles);
        this.loadingVehicles.set(false);
        // Re-apply on the next tick: the <select>'s <option> elements render
        // from this signal update on the NEXT change-detection cycle, so
        // patching synchronously here still has nothing to match yet.
        if (this.savedFrontVehicleCodeId != null) {
          setTimeout(() => this.form.patchValue({ frontVehicleCodeId: this.savedFrontVehicleCodeId }));
        }
      },
      error: () => { this.loadingVehicles.set(false); }
    });
  }

  save(): void {
    this.saving.set(true);
    this.error.set('');
    const raw = this.form.getRawValue();
    const pickedVehicle = this.directPortEntrance() && raw.frontVehicleCodeId
      ? this.vehicleOptions().find(v => v.Id === Number(raw.frontVehicleCodeId))
      : undefined;
    const model: TruckAnnouncementPin = {
      Id: Number(this.pinId),
      PIN: raw.pin || undefined,
      LicencePlate: this.directPortEntrance() ? undefined : (raw.licencePlate || undefined),
      // Not yet persisted by the API (TruckAnnouncementPin edit DTO doesn't expose it) — sent so
      // this starts working automatically once the backend adds support. See BUGREPORT.
      FrontVehicleCodeId: this.directPortEntrance() ? (Number(raw.frontVehicleCodeId) || undefined) : undefined,
      FrontVehicleCode: pickedVehicle ? (pickedVehicle.Name || String(pickedVehicle.Code)) : undefined,
      RealizedLicencePlate: raw.realizedLicencePlate || undefined,
      PermitUserBadgeNumber: raw.permitUserBadgeNumber || undefined,
      AnnouncementFrom: this.api.toApiDate(raw.announcementFrom),
      AnnouncementTo: this.api.toApiDate(raw.announcementTo),
      ServiceRequest: raw.serviceRequest || undefined,
      ServiceRequestPCSId: raw.serviceRequestPCSId ?? undefined
    };
    this.api.editPin(model).subscribe({
      next: () => {
        this.saving.set(false);
        void this.router.navigate(this.backLink());
      },
      error: err => { this.error.set(err?.error?.Message ?? err?.error?.message ?? 'ta.pinSaveError'); this.saving.set(false); }
    });
  }

  private patch(pin: TruckAnnouncementPin): void {
    this.form.patchValue({
      pin: pin.PIN ?? '',
      licencePlate: pin.LicencePlate ?? '',
      frontVehicleCodeId: pin.FrontVehicleCodeId ?? '',
      realizedLicencePlate: pin.RealizedLicencePlate ?? '',
      permitUserBadgeNumber: pin.PermitUserBadgeNumber ?? '',
      announcementFrom: this.api.toLocalInputDate(pin.AnnouncementFrom),
      announcementTo: this.api.toLocalInputDate(pin.AnnouncementTo),
      serviceRequest: pin.ServiceRequest ?? '',
      serviceRequestPCSId: pin.ServiceRequestPCSId ?? null
    });
  }
}
